import { createContext, useContext, useEffect, useRef, useCallback } from "react";
import type { ReactNode } from "react";
import { io, type Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../store/auth";
import { useToast } from "../components/Toast";

const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL ??
  (import.meta.env.PROD ? "https://kkks-admin-app.vercel.app" : "http://localhost:3000");

const SocketCtx = createContext<Socket | null>(null);
const LALAMOVE_FAILED_STATUSES = new Set(["CANCELED", "REJECTED", "EXPIRED"]);

export function SocketProvider({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const ref = useRef<Socket | null>(null);
  const toast = useToast();

  // A Lalamove delivery reaching a terminal failure previously produced no
  // signal at all here — admin would only see it by opening that exact
  // order's detail screen. This is the oversight/monitoring role, so a
  // visible banner (no sound — admin isn't expected to react as urgently
  // as the counter staff who'd actually re-dispatch) is enough.
  const flagDeliveryFailed = useCallback((orderCode: string) => {
    toast.show(`Order ${orderCode}: Lalamove delivery failed — needs manual re-dispatch`, "error");
  }, [toast]);

  useEffect(() => {
    if (!token) return;
    const sock = io(`${SOCKET_URL}/admin`, { auth: { token }, transports: ["websocket", "polling"] });
    ref.current = sock;

    // Invalidate order queries on key events
    sock.on("order:new", () => void qc.invalidateQueries({ queryKey: ["admin", "orders"] }));
    sock.on("order:status_changed", () => void qc.invalidateQueries({ queryKey: ["admin", "orders"] }));
    sock.on("transaction:paid", () => {
      void qc.invalidateQueries({ queryKey: ["admin", "transactions"] });
      void qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    });
    sock.on("rider:status_changed", (payload: { orderCode?: string; status?: string }) => {
      if (LALAMOVE_FAILED_STATUSES.has(payload?.status ?? "")) {
        flagDeliveryFailed(payload?.orderCode ?? "");
        void qc.invalidateQueries({ queryKey: ["notifications"] });
      }
      void qc.invalidateQueries({ queryKey: ["admin", "orders"] });
    });

    return () => { sock.disconnect(); ref.current = null; };
  }, [token, qc, flagDeliveryFailed]);

  return <SocketCtx.Provider value={ref.current}>{children}</SocketCtx.Provider>;
}

export function useAdminSocket(): Socket | null {
  return useContext(SocketCtx);
}
